import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getActiveGamesOptions } from '@api/generated/@tanstack/react-query.gen';
import type { GameContextValue } from './game.types';

/** Mirrors `webapp:accessToken` in `api-client-config`. */
const ACTIVE_GAME_KEY = 'webapp:activeGameId';

/**
 * The seeded game, used only until `GET /games` resolves on a cold first load
 * (and if that request fails). It matches the `games` row every existing record
 * was backfilled to, so the first paint is correct rather than blank — the
 * landing page renders this copy before any request completes.
 */
const SEEDED_GAME_ID = '2xko';
const SEEDED_GAME_NAME = '2XKO';

const GameContext = createContext<GameContextValue | null>(null);

function readStoredGameId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_GAME_KEY);
  } catch {
    // Private-mode Safari and similar can throw on access; the seeded default
    // is a fine answer.
    return null;
  }
}

export function GameProvider({ children }: { children: ReactNode }) {
  const [selectedGameId, setSelectedGameId] = useState<string | null>(
    readStoredGameId,
  );

  const { data, isLoading } = useQuery({
    ...getActiveGamesOptions(),
    // The registered games change on the order of never, and this runs on the
    // public landing page — don't refetch it on every focus.
    staleTime: 1000 * 60 * 60,
  });

  const games = useMemo(() => data ?? [], [data]);

  // Drop a stored selection that is no longer registered (a game was retired,
  // or the value predates a rename) rather than filtering everything by an id
  // the API will never return.
  useEffect(() => {
    if (!selectedGameId || games.length === 0) return;
    if (games.some((game) => game.id === selectedGameId)) return;

    setSelectedGameId(null);
    try {
      localStorage.removeItem(ACTIVE_GAME_KEY);
    } catch {
      // Nothing to do — the invalid id is already dropped from state.
    }
  }, [games, selectedGameId]);

  const activeGame = useMemo(() => {
    if (games.length === 0) return null;
    if (selectedGameId) {
      const stored = games.find((game) => game.id === selectedGameId);
      if (stored) return stored;
    }
    return games[0];
  }, [games, selectedGameId]);

  const setActiveGameId = useCallback((gameId: string) => {
    setSelectedGameId(gameId);
    try {
      localStorage.setItem(ACTIVE_GAME_KEY, gameId);
    } catch {
      // Selection still applies for this session.
    }
  }, []);

  const value = useMemo<GameContextValue>(
    () => ({
      games,
      activeGame,
      activeGameId: activeGame?.id ?? selectedGameId ?? SEEDED_GAME_ID,
      activeGameName: activeGame?.name ?? SEEDED_GAME_NAME,
      hasMultipleGames: games.length > 1,
      isLoading,
      setActiveGameId,
    }),
    [games, activeGame, selectedGameId, isLoading, setActiveGameId],
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame(): GameContextValue {
  const context = useContext(GameContext);

  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }

  return context;
}
